import * as R from "ramda";
import type { Entity } from "common/entities/types";
import { getAllRequiredColumns } from "common/form/functions/layout";
import { LayoutActionName } from "common/form/types";
import {
  getActionLayout,
  getRecordToValidate,
} from "common/record/actions/ui/work-order-action/functions";
import { WorkOrderActionRecord } from "common/record/actions/ui/work-order-action/types";
import { getFormOrDefault } from "common/record/edit/functions";
import type { Context } from "common/types/context";
import { Form } from "common/types/forms";
import { Properties } from "common/types/records";

export function getRequiredEntitiesFromLayout(
  context: Context,
  entity: Entity,
  actionName: LayoutActionName,
  properties: Properties,
) {
  const actionLayout = getActionLayout(context, entity, actionName, properties);
  return (
    actionLayout?.requiredEntities
      ?.map((e) => context.entities[e])
      .filter((e) => !!e) ?? []
  );
}

export function hasMissingRequiredProperties(
  entity: Entity,
  form: Form,
  properties: Properties,
) {
  const allRequiredColumns = getAllRequiredColumns(form?.settings, entity);
  return (
    allRequiredColumns?.length !== 0 &&
    allRequiredColumns.some(
      (column) => R.isNil(properties[column]) || properties[column] === "",
    )
  );
}

export function hasMissingRequiredRecords(
  context: Context,
  relatedEntity: Entity,
  record: WorkOrderActionRecord,
) {
  const recordToValidate = getRecordToValidate(record);
  const relatedRecords = recordToValidate.related?.[relatedEntity.name] ?? [];
  const notArchivedRecords = relatedRecords.filter(
    (r) => !r.deleted && !r.properties?.isDeleted,
  );

  if (notArchivedRecords?.length === 0) return true;

  return notArchivedRecords.some((r) => {
    const formId = r.properties?.formId;
    const form = getFormOrDefault(context, relatedEntity.name, formId);

    return hasMissingRequiredProperties(relatedEntity, form, r.properties);
  });
}

export function getMissingRequiredEntities(
  context: Context,
  entity: Entity,
  actionName: LayoutActionName,
  record: WorkOrderActionRecord,
) {
  const recordToValidate = getRecordToValidate(record);
  const requiredEntities = getRequiredEntitiesFromLayout(
    context,
    entity,
    actionName,
    recordToValidate?.fullRecord?.properties ?? recordToValidate?.properties,
  );

  if (!recordToValidate?.related && !recordToValidate?.relatedUiValue?.form)
    return requiredEntities;

  return requiredEntities.filter((relatedEntity) =>
    hasMissingRequiredRecords(context, relatedEntity, record),
  );
}
